import { h, apiClient, sectionHeader, statTile, btn, toast, openModal, openConfirm, emptyState, skeletonCard } from './_common.js';

const eur = (n) => (n === null || n === undefined || isNaN(n)) ? '—' : Number(n).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
const CATEGORIAS = ['Embutidos', 'Congelados', 'Frescos', 'Conservas', 'Bebidas', 'Pan', 'Aceites y salsas', 'Otros'];
const UNIDADES = ['kg', 'g', 'L', 'ud', 'docena'];

// Tema de color de la carta (granate + crema/mármol + oro) SOLO para la sección Bodega.
function injectBodegaTheme() {
  if (document.getElementById('bodega-carta-theme-css')) return;
  const st = document.createElement('style');
  st.id = 'bodega-carta-theme-css';
  st.textContent = [
    '.bodega-carta-theme{background:linear-gradient(160deg,#f8f4ee,#efe6d4);color:#2a201d;border:1px solid #e3d5b8;box-shadow:0 8px 30px rgba(0,0,0,.25)}',
    '.bodega-carta-theme [class*="bg-base-900"]{background:#fbf7ef !important}',
    '.bodega-carta-theme [class*="bg-base-800"]{background:#f1e8d6 !important}',
    '.bodega-carta-theme [class*="bg-base-700"]{background:#e9dcc2 !important}',
    '.bodega-carta-theme [class*="text-ink-100"]{color:#2a201d !important}',
    '.bodega-carta-theme [class*="text-ink-200"]{color:#3a2a1c !important}',
    '.bodega-carta-theme [class*="text-ink-300"]{color:#6b4a30 !important}',
    '.bodega-carta-theme [class*="text-ink-400"]{color:#8a6a4f !important}',
    '.bodega-carta-theme [class*="text-ink-500"]{color:#9a8467 !important}',
    '.bodega-carta-theme [class*="text-gold-3"],.bodega-carta-theme [class*="text-gold-2"]{color:#7a2130 !important}',
    '.bodega-carta-theme [class*="border-base-7"]{border-color:#e3d5b8 !important}',
    '.bodega-carta-theme [class*="border-base-8"]{border-color:#ece0c8 !important}',
    '.bodega-carta-theme [class*="border-base-6"]{border-color:#d8c8a8 !important}',
    '.bodega-carta-theme [class*="border-gold-4"]{border-color:#b0873f !important}',
    '.bodega-carta-theme [class*="bg-gold-4"]{background:rgba(176,135,63,.14) !important}',
  ].join('');
  document.head.appendChild(st);
}

export async function renderBodega(root) {
  root.innerHTML = '';
  injectBodegaTheme();
  const wrap = h('div', { class: 'animate-fade-in bodega-carta-theme rounded-2xl p-4 sm:p-6' });
  root.appendChild(wrap);
  const todayISO = () => { const t = new Date(); t.setMinutes(t.getMinutes() - t.getTimezoneOffset()); return t.toISOString().slice(0, 10); };
  const state = { tab: 'home', productos: [], platos: [], mesas: [], reservas: [], resFecha: todayISO() };
  // Modo solo-lectura (usuario "solo bodega", p.ej. el padre): ve todo pero no edita.
  const RO = !!(typeof window !== 'undefined' && window.__BODEGA_READONLY__);

  async function load() {
    const [pr, pl, me] = await Promise.all([
      apiClient.get('/api/v1/bodega/productos').catch(() => ({ productos: [] })),
      apiClient.get('/api/v1/bodega/platos').catch(() => ({ platos: [] })),
      apiClient.get('/api/v1/bodega/mesas').catch(() => ({ mesas: [] })),
    ]);
    state.productos = pr.productos || [];
    state.platos = pl.platos || [];
    state.mesas = me.mesas || [];
  }

  async function loadReservas(fecha) {
    const r = await apiClient.get('/api/v1/bodega/reservas?fecha=' + encodeURIComponent(fecha)).catch(() => ({ reservas: [] }));
    state.reservas = r.reservas || [];
    state.resLoadedFor = fecha;
  }

    const MENU = [
      { id: 'costes', emoji: '💶', title: 'Costes', desc: 'Coste por producto (gramos y tapa) y por plato.', help: 'Dentro tienes dos desplegables: PRODUCTOS (lo que cuesta cada cosa por gramo, tapa y ración, con calculadora) y PLATOS (lo que cuesta cada plato y lo que ganas). Toca cada uno para abrirlo.' },
      { id: 'carta', emoji: '🍷', title: 'Carta y QR', desc: 'La carta de los clientes y el QR de las mesas.', help: 'La carta que ven los clientes en el móvil y el código QR de las mesas. Toca "Ver carta" para abrirla.' },
      { id: 'reservas', emoji: '📅', title: 'Reservas', desc: 'Las reservas que hacen los clientes en la web, por día.', help: 'Aquí ves todas las reservas que llegan desde la web, día por día. Puedes confirmarlas, cancelarlas, marcar "no vino" y (cuando esté el plano) asignarles mesa. También puedes añadir una reserva a mano.' },
      { id: 'productos', emoji: '📦', title: 'Productos', desc: 'Todo lo que compras con su precio (editar precios).', help: 'Todo lo que compras con su precio. Aquí se cambian los precios de compra si sube un proveedor.' },
    ];

  function draw() {
    wrap.innerHTML = '';
    const conEscandallo = state.platos.filter((p) => p.coste_racion !== null);
    const margenMedio = conEscandallo.length
      ? conEscandallo.reduce((s, p) => s + (p.margen_pct || 0), 0) / conEscandallo.length : null;

    // ---------- INICIO: menú con 4 opciones ----------
    if (state.tab === 'home') {
      wrap.appendChild(h('div', { class: 'flex items-center gap-2 mb-1' }, [
        h('span', { class: 'text-2xl' }, '🍷'),
        h('div', { class: 'font-display font-bold text-ink-100 text-lg' }, 'Bodega 7 Hermanos'),
      ]));
      wrap.appendChild(h('div', { class: 'text-[11px] text-ink-400 mb-4' },
        '📦 ' + state.productos.length + ' productos · 🍽️ ' + state.platos.length + ' platos · 🧮 ' + conEscandallo.length + ' con coste · 📈 ' + (margenMedio === null ? '—' : margenMedio.toFixed(0) + '% ganancia')));
      wrap.appendChild(h('div', { class: 'grid grid-cols-1 sm:grid-cols-2 gap-2.5' }, MENU.map((m) =>
        h('button', {
          class: 'bg-base-900 border border-base-700 rounded-2xl p-4 text-left flex items-center gap-3 w-full active:scale-[.99] transition',
          onclick: () => { state.tab = m.id; draw(); },
        }, [
          h('div', { class: 'text-2xl shrink-0' }, m.emoji),
          h('div', { class: 'min-w-0 flex-1' }, [
            h('div', { class: 'font-semibold text-ink-100' }, m.title),
            h('div', { class: 'text-[11px] text-ink-400 mt-0.5 break-words leading-snug' }, m.desc),
          ]),
          h('div', { class: 'text-ink-500 text-xl shrink-0' }, '›'),
        ])
      )));
      return;
    }

    // ---------- DETALLE: una sección con botón volver ----------
    const cur = MENU.find((m) => m.id === state.tab) || {};
    wrap.appendChild(h('button', { class: 'mb-2 text-gold-300 text-sm font-semibold', onclick: () => { state.tab = 'home'; draw(); } }, '‹ Volver'));
    wrap.appendChild(h('div', { class: 'font-display font-bold text-ink-100 text-lg mb-2' }, (cur.emoji || '') + ' ' + (cur.title || '')));
    if (cur.help) {
      wrap.appendChild(h('div', { class: 'bg-gold-400/10 border border-gold-400/25 rounded-lg px-3 py-2 mb-3 text-xs text-ink-300 flex gap-2 items-start leading-snug' },
        [ h('span', {}, 'ℹ️'), h('span', {}, cur.help) ]));
    }
    if (!RO && state.tab === 'productos') wrap.appendChild(h('div', { class: 'mb-3' }, btn('+ Añadir producto', () => openProductoModal(), {})));

    wrap.appendChild(
      state.tab === 'productos' ? drawProductos()
      : state.tab === 'carta' ? drawCarta()
      : state.tab === 'reservas' ? drawReservas()
      : drawCostes());
  }

  // ---------- RESERVAS ----------
  const RESERVAR_URL = 'https://carta.178-105-147-16.sslip.io/reservar.html';
  const RES_QR_URL = 'https://carta.178-105-147-16.sslip.io/img/qr-reservas.png';
  const ESTADOS = {
    pendiente: { label: 'Pendiente', cls: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
    confirmada: { label: 'Confirmada', cls: 'bg-emerald-600/20 text-emerald-300 border-emerald-600/40' },
    sentada: { label: 'En mesa', cls: 'bg-sky-600/20 text-sky-300 border-sky-600/40' },
    cancelada: { label: 'Cancelada', cls: 'bg-base-700/40 text-ink-400 border-base-600' },
    no_show: { label: 'No vino', cls: 'bg-red-600/20 text-red-300 border-red-600/40' },
  };
  const fmtFechaLarga = (iso) => {
    const p = iso.split('-'); const d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
    return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
  };
  const shiftFecha = (iso, days) => { const p = iso.split('-'); const d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2])); d.setDate(d.getDate() + days); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 10); };

  function drawReservas() {
    const box = h('div', { class: 'space-y-4' });

    // --- Cabecera: enlace + QR de la web de reservas ---
    const head = h('div', { class: 'bg-base-900 border border-base-700 rounded-2xl p-4 flex flex-col sm:flex-row gap-4 items-center' }, [
      h('img', { src: RES_QR_URL, alt: 'QR reservas', class: 'w-24 h-24 rounded-lg bg-white p-1.5 shrink-0', onerror: function () { this.style.display = 'none'; } }),
      h('div', { class: 'flex-1 min-w-0 text-center sm:text-left' }, [
        h('div', { class: 'font-display font-bold text-ink-100' }, '📅 Reservas online'),
        h('div', { class: 'text-[11px] text-ink-400 mt-0.5' }, 'Los clientes reservan desde esta web. Cada reserva aparece aquí y te llega aviso a Telegram.'),
        h('div', { class: 'text-[11px] bg-base-800 rounded-lg px-2.5 py-1.5 text-gold-300 break-all font-mono mt-2' }, RESERVAR_URL),
        h('a', { href: RESERVAR_URL, target: '_blank', rel: 'noopener', class: 'inline-block mt-2 bg-gradient-to-r from-gold-400 to-gold-500 text-base-900 font-semibold px-3 py-1.5 rounded-lg text-xs' }, 'Abrir web de reservas'),
      ]),
    ]);
    box.appendChild(head);

    // --- Navegador de día ---
    const nav = h('div', { class: 'flex items-center justify-between gap-2 bg-base-800/50 border border-base-700 rounded-xl px-2 py-2' });
    const label = h('div', { class: 'flex-1 text-center' }, [
      h('div', { class: 'font-semibold text-ink-100 capitalize text-sm' }, fmtFechaLarga(state.resFecha)),
      h('div', { class: 'text-[10px] text-ink-400' }, state.resFecha === todayISO() ? 'Hoy' : ''),
    ]);
    const go = async (fecha) => { state.resFecha = fecha; await loadReservas(fecha); draw(); };
    nav.appendChild(h('button', { class: 'px-3 py-2 text-ink-300 text-lg active:scale-95', onclick: () => go(shiftFecha(state.resFecha, -1)) }, '‹'));
    nav.appendChild(label);
    nav.appendChild(h('button', { class: 'px-3 py-2 text-ink-300 text-lg active:scale-95', onclick: () => go(shiftFecha(state.resFecha, 1)) }, '›'));
    box.appendChild(nav);

    const tools = h('div', { class: 'flex items-center gap-2 flex-wrap' }, [
      (() => { const inp = h('input', { type: 'date', value: state.resFecha, class: 'bg-base-800 border border-base-700 rounded-lg px-2 py-1.5 text-sm text-ink-100' }); inp.addEventListener('change', () => inp.value && go(inp.value)); return inp; })(),
      state.resFecha !== todayISO() ? btn('Hoy', () => go(todayISO()), { variant: 'ghost' }) : null,
      RO ? null : btn('+ Reserva a mano', () => openReservaModal(), {}),
    ]);
    box.appendChild(tools);

    // --- Plano de mesas (vista cenital) ---
    if (state.mesas.length) {
      box.appendChild(collapsibleSection('🗺️ Plano de mesas', 'Rojo = con reserva · Toca una mesa para asignar o liberar una reserva del día', planoBox(), true));
    }

    // --- Lista ---
    const listWrap = h('div', { class: 'space-y-2' });
    box.appendChild(listWrap);
    if (state.resLoadedFor !== state.resFecha) {
      listWrap.appendChild(h('div', { class: 'text-center text-ink-400 text-sm py-6' }, 'Cargando reservas…'));
      loadReservas(state.resFecha).then(() => { if (state.tab === 'reservas') draw(); });
    } else {
      renderReservasList(listWrap);
    }
    return box;
  }

  // Vista cenital del local con las mesas del esquema de Carlos.
  function planoBox() {
    const cont = h('div', { class: 'relative w-full rounded-xl overflow-hidden', style: 'aspect-ratio:16/10;background:#f4efe6;border:1px solid #d8cbb0;box-shadow:inset 0 0 40px rgba(120,90,60,.08)' });
    // Pared en esquina (L, rojo)
    cont.appendChild(h('div', { style: 'position:absolute;left:55%;top:20%;width:42%;height:3px;background:#c0453f;border-radius:2px' }));
    cont.appendChild(h('div', { style: 'position:absolute;left:55%;top:20%;width:3px;height:58%;background:#c0453f;border-radius:2px' }));
    cont.appendChild(h('div', { style: 'position:absolute;left:6%;top:1.5%;font-family:Oswald,sans-serif;font-weight:300;font-size:9px;letter-spacing:.18em;color:#9a8467' }, 'BARRA'));

    for (const m of state.mesas) {
      const occ = state.reservas.find((r) => r.mesa_id === m.id && r.estado !== 'cancelada');
      const isBar = m.zona === 'barra';
      const size = isBar ? 26 : 46;
      const bg = occ ? '#5e1622' : '#ffffff';
      const bd = occ ? '#5e1622' : '#b0873f';
      const fg = occ ? '#ffffff' : '#5e1622';
      const radius = m.forma === 'redonda' ? '50%' : '8px';
      const el = h('button', {
        style: `position:absolute;left:${m.x}%;top:${m.y}%;width:${size}px;height:${size}px;border:1.5px solid ${bd};background:${bg};color:${fg};border-radius:${radius};display:flex;flex-direction:column;align-items:center;justify-content:center;line-height:1.05;font-family:Oswald,sans-serif;box-shadow:0 2px 6px rgba(0,0,0,.14);cursor:${RO ? 'default' : 'pointer'};padding:0`,
        title: m.nombre + (occ ? ' · ' + occ.nombre + ' (' + occ.personas + 'p)' : ' · libre'),
        onclick: RO ? null : () => openMesaAsignar(m),
      }, isBar
        ? [h('div', { style: 'font-size:11px' }, '🪑')]
        : [
            h('div', { style: 'font-size:11px;font-weight:600' }, m.nombre.replace('Mesa ', 'M')),
            occ
              ? h('div', { style: 'font-size:8px;font-weight:400;max-width:42px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap' }, occ.nombre.split(' ')[0])
              : h('div', { style: 'font-size:8px;font-weight:400;color:#a8895f' }, m.capacidad + 'p'),
          ]);
      cont.appendChild(el);
    }
    const ocupadas = state.mesas.filter((m) => state.reservas.some((r) => r.mesa_id === m.id && r.estado !== 'cancelada')).length;
    const leg = h('div', { class: 'flex items-center gap-3 text-[10px] text-ink-400 mt-2 px-1 flex-wrap' }, [
      h('span', {}, '⬜ libre'), h('span', {}, '🟥 con reserva'),
      h('span', { class: 'ml-auto' }, ocupadas + '/' + state.mesas.length + ' mesas ocupadas'),
    ]);
    return h('div', {}, [cont, leg]);
  }

  async function openMesaAsignar(m) {
    const occ = state.reservas.find((r) => r.mesa_id === m.id && r.estado !== 'cancelada');
    if (occ) {
      const ok = await openConfirm({ title: m.nombre + ' · ' + occ.nombre, message: occ.personas + ' personas · ' + occ.hora + ' h. ¿Liberar esta mesa?', danger: true });
      if (ok) { await apiClient.put('/api/v1/bodega/reservas/' + occ.id, { mesaId: '' }); toast('Mesa liberada'); await loadReservas(state.resFecha); draw(); }
      return;
    }
    const libres = state.reservas.filter((r) => !r.mesa_id && r.estado !== 'cancelada');
    if (!libres.length) { toast('No hay reservas sin mesa este día'); return; }
    const labels = libres.map((r) => r.hora + ' — ' + r.nombre + ' (' + r.personas + 'p)');
    await openModal({
      title: 'Asignar a ' + m.nombre, icon: '🪑',
      subtitle: 'Elige la reserva para esta mesa (' + m.capacidad + ' plazas).',
      fields: [{ name: 'sel', label: 'Reserva', type: 'select', options: labels, value: labels[0] }],
      onSubmit: async (v) => {
        const r = libres[labels.indexOf(v.sel)];
        if (r) { await apiClient.put('/api/v1/bodega/reservas/' + r.id, { mesaId: m.id }); toast('Asignada a ' + m.nombre); await loadReservas(state.resFecha); draw(); }
      },
    });
  }

  function renderReservasList(listWrap) {
    listWrap.innerHTML = '';
    const activas = state.reservas.filter((r) => r.estado !== 'cancelada');
    const pendientes = activas.filter((r) => r.estado === 'pendiente').length;
    const personas = activas.reduce((s, r) => s + (Number(r.personas) || 0), 0);
    listWrap.appendChild(h('div', { class: 'text-[11px] text-ink-400 px-1' },
      state.reservas.length + ' reserva' + (state.reservas.length === 1 ? '' : 's') + ' · ' + personas + ' comensales' + (pendientes ? ' · ⚠️ ' + pendientes + ' sin confirmar' : '')));

    if (!state.reservas.length) {
      listWrap.appendChild(emptyState('📅', 'Sin reservas este día', 'Cuando alguien reserve en la web aparecerá aquí. También puedes añadir una a mano.',
        RO ? null : btn('+ Añadir reserva', () => openReservaModal(), {})));
      return;
    }

    const orden = { pendiente: 0, confirmada: 1, sentada: 2, no_show: 3, cancelada: 4 };
    const sorted = [...state.reservas].sort((a, b) => (a.hora || '').localeCompare(b.hora || '') || (orden[a.estado] - orden[b.estado]));
    for (const r of sorted) {
      listWrap.appendChild(reservaCard(r));
    }
  }

  function reservaCard(r) {
    const est = ESTADOS[r.estado] || ESTADOS.pendiente;
    const mesa = state.mesas.find((m) => m.id === r.mesa_id);
    const cancelled = r.estado === 'cancelada';

    const top = h('div', { class: 'flex items-start justify-between gap-2' }, [
      h('div', { class: 'min-w-0 flex-1' }, [
        h('div', { class: 'flex items-center gap-2 flex-wrap' }, [
          h('span', { class: 'font-display font-bold text-ink-100 ' + (cancelled ? 'line-through text-ink-500' : '') }, (r.hora || '--:--')),
          h('span', { class: 'text-ink-200 break-words ' + (cancelled ? 'line-through text-ink-500' : '') }, r.nombre),
          h('span', { class: 'text-[10px] px-1.5 py-0.5 rounded border ' + est.cls }, est.label),
          r.ocasion === 'cumpleanos' ? h('span', { class: 'text-[10px] px-1.5 py-0.5 rounded bg-pink-500/20 text-pink-300 border border-pink-500/40' }, '🎂 Cumple') : null,
          r.ocasion === 'evento' ? h('span', { class: 'text-[10px] px-1.5 py-0.5 rounded bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/40' }, '🎉 Evento') : null,
          r.origen === 'manual' ? h('span', { class: 'text-[10px] text-ink-500' }, '✍️ a mano') : null,
        ]),
        h('div', { class: 'text-[11px] text-ink-400 mt-1 flex items-center gap-2 flex-wrap' }, [
          h('span', {}, '👥 ' + r.personas),
          r.telefono ? h('a', { href: 'tel:' + r.telefono, class: 'text-gold-300' }, '📞 ' + r.telefono) : null,
          r.email ? h('a', { href: 'mailto:' + r.email, class: 'text-gold-300 break-all' }, '✉️ ' + r.email) : null,
          mesa ? h('span', { class: 'text-sky-300' }, '🪑 ' + mesa.nombre) : null,
        ]),
        r.notas ? h('div', { class: 'text-[11px] text-amber-300/80 mt-1 break-words' }, '📝 ' + r.notas) : null,
      ]),
    ]);

    const card = h('div', { class: 'bg-base-900 border border-base-700 rounded-xl p-3' }, [top]);
    card.dataset.q = ((r.nombre || '') + ' ' + (r.telefono || '')).toLowerCase();
    if (RO) return card;

    // Acciones
    const acts = h('div', { class: 'flex items-center gap-1.5 flex-wrap mt-2.5 pt-2.5 border-t border-base-800' });
    const setEstado = async (estado) => { await apiClient.put('/api/v1/bodega/reservas/' + r.id, { estado }); toast('Reserva ' + (ESTADOS[estado] ? ESTADOS[estado].label.toLowerCase() : estado)); await loadReservas(state.resFecha); draw(); };
    const chip = (txt, estado, on) => h('button', { class: 'text-[11px] px-2.5 py-1 rounded-lg border ' + (on ? est.cls : 'bg-base-800 border-base-700 text-ink-300'), onclick: () => setEstado(estado) }, txt);

    if (r.estado !== 'cancelada') {
      if (r.estado === 'pendiente') acts.appendChild(chip('✓ Confirmar', 'confirmada', false));
      if (r.estado === 'confirmada') acts.appendChild(chip('🪑 Sentar', 'sentada', false));
      if (r.estado === 'sentada') acts.appendChild(chip('↩︎ Reabrir', 'confirmada', false));
      acts.appendChild(chip('✕ No vino', 'no_show', r.estado === 'no_show'));
      acts.appendChild(chip('Cancelar', 'cancelada', false));
    } else {
      acts.appendChild(chip('↩︎ Reactivar', 'pendiente', false));
    }

    // Asignar mesa (si hay mesas definidas)
    if (state.mesas.length) {
      const sel = h('select', { class: 'text-[11px] bg-base-800 border border-base-700 rounded-lg px-2 py-1 text-ink-200' }, [
        h('option', { value: '' }, 'Sin mesa'),
        ...state.mesas.map((m) => h('option', { value: m.id, selected: m.id === r.mesa_id ? 'selected' : null }, m.nombre + ' (' + m.capacidad + ')')),
      ]);
      sel.addEventListener('change', async () => { await apiClient.put('/api/v1/bodega/reservas/' + r.id, { mesaId: sel.value }); toast('Mesa asignada'); await loadReservas(state.resFecha); draw(); });
      acts.appendChild(sel);
    }

    acts.appendChild(h('button', { class: 'text-[11px] text-ink-500 hover:text-red-300 ml-auto', onclick: () => delReserva(r) }, '🗑️'));
    card.appendChild(acts);
    return card;
  }

  async function openReservaModal(r) {
    await openModal({
      title: r ? 'Editar reserva' : 'Nueva reserva', icon: '📅',
      subtitle: r ? '' : 'Añade una reserva tomada por teléfono o en persona.',
      fields: [
        { name: 'nombre', label: 'Nombre', required: true, value: r?.nombre || '', placeholder: 'Nombre del cliente' },
        { name: 'telefono', label: 'Teléfono', required: true, value: r?.telefono || '', placeholder: '600 00 00 00' },
        { name: 'email', label: 'Email (opcional)', value: r?.email || '', placeholder: 'nombre@gmail.com' },
        { name: 'personas', label: 'Comensales', type: 'number', required: true, value: r?.personas ?? 2, placeholder: '2' },
        { name: 'fecha', label: 'Fecha', type: 'date', required: true, value: r?.fecha || state.resFecha },
        { name: 'hora', label: 'Hora', value: r?.hora || '21:00', placeholder: '21:00', help: 'Formato 24h, ej. 14:30 o 21:00' },
        { name: 'ocasion', label: 'Ocasión', type: 'select', options: ['normal', 'cumpleanos', 'evento'], value: r?.ocasion || 'normal', help: 'cumpleanos = cumpleaños · evento = grupo/celebración' },
        { name: 'notas', label: 'Comentario', value: r?.notas || '', placeholder: 'Terraza, celebración, alérgenos…' },
      ],
      onSubmit: async (v) => {
        const body = { nombre: v.nombre, telefono: v.telefono, email: v.email, personas: Number(v.personas), fecha: v.fecha, hora: v.hora, ocasion: v.ocasion, notas: v.notas };
        if (r) await apiClient.put('/api/v1/bodega/reservas/' + r.id, body);
        else await apiClient.post('/api/v1/bodega/reservas', body);
        toast(r ? 'Reserva actualizada' : 'Reserva añadida');
        if (!r && v.fecha !== state.resFecha) state.resFecha = v.fecha;
        await loadReservas(state.resFecha); draw();
      },
    });
  }

  async function delReserva(r) {
    const ok = await openConfirm({ title: 'Borrar reserva de "' + r.nombre + '"', message: 'Se eliminará definitivamente. Si solo quieres marcarla, usa "Cancelar".', danger: true });
    if (!ok) return;
    await apiClient.delete('/api/v1/bodega/reservas/' + r.id);
    toast('Reserva borrada'); await loadReservas(state.resFecha); draw();
  }

  // ---------- COSTES: dos desplegables (Productos + Platos) ----------
  function drawCostes() {
    const box = h('div', { class: 'space-y-3' });
    box.appendChild(collapsibleSection('📦 PRODUCTOS', 'Lo que cuesta cada cosa por gramo, tapa y ración · con calculadora', drawCostesTapa(), true));
    const platosContent = h('div', {});
    if (!RO) platosContent.appendChild(h('div', { class: 'mb-3' }, btn('+ Añadir plato', () => openPlatoModal(), {})));
    platosContent.appendChild(drawEscandallos());
    box.appendChild(collapsibleSection('🍽️ PLATOS', 'Lo que cuesta cada plato y lo que ganas', platosContent, false));
    return box;
  }

  function collapsibleSection(title, desc, content, open) {
    const body = h('div', { class: (open ? '' : 'hidden') + ' px-3 pb-3' }, [content]);
    const chev = h('span', { class: 'text-ink-400 text-base shrink-0' }, open ? '▴' : '▾');
    const header = h('button', {
      class: 'w-full flex items-center justify-between gap-2 px-3 py-3 text-left',
      onclick: () => { body.classList.toggle('hidden'); chev.textContent = body.classList.contains('hidden') ? '▾' : '▴'; },
    }, [
      h('div', { class: 'min-w-0' }, [
        h('div', { class: 'font-display font-bold text-ink-100' }, title),
        desc ? h('div', { class: 'text-[11px] text-ink-400 mt-0.5 break-words' }, desc) : null,
      ]),
      chev,
    ]);
    return h('div', { class: 'bg-base-800/40 border border-base-700 rounded-2xl overflow-hidden' }, [header, body]);
  }

  // ---------- PRODUCTOS ----------
  function drawProductos() {
    if (!state.productos.length) {
      return emptyState('📦', 'Sin productos todavía', 'Añade lo que compras (ej. "Calamar caja 5 kg — 32 €") y calculo el coste por kg/unidad.',
        RO ? null : btn('+ Añadir primer producto', () => openProductoModal(), {}));
    }
    const cards = state.productos.map((p) => {
      const card = h('div', { class: 'bg-base-900 border border-base-700 rounded-xl p-3 flex items-start justify-between gap-2' }, [
        h('div', { class: 'min-w-0 flex-1' }, [
          h('div', { class: 'font-semibold text-ink-100 break-words text-sm' }, p.nombre),
          h('div', { class: 'text-[11px] text-ink-400 mt-0.5 break-words' }, (p.categoria || '') + (p.formato ? ' · ' + p.formato : '')),
          p.notas ? h('div', { class: 'text-[11px] text-amber-300/80 mt-0.5 break-words' }, p.notas) : null,
        ]),
        h('div', { class: 'text-right shrink-0' }, [
          h('div', { class: 'text-gold-300 font-bold text-sm' }, eur(p.coste_unitario) + '/' + (p.unidad || 'ud')),
          h('div', { class: 'text-[10px] text-ink-400' }, 'compra ' + eur(p.precio_compra)),
          RO ? null : h('div', { class: 'flex gap-3 justify-end mt-2' }, [
            iconBtn('✏️', () => openProductoModal(p)),
            iconBtn('🗑️', () => delProducto(p)),
          ]),
        ]),
      ]);
      card.dataset.q = (p.nombre + ' ' + (p.categoria || '')).toLowerCase();
      return card;
    });
    const list = h('div', { class: 'space-y-2' }, cards);
    return h('div', {}, [ searchBox('🔎 Buscar producto…', cards), list ]);
  }

  async function openProductoModal(p) {
    await openModal({
      title: p ? 'Editar producto' : 'Nuevo producto', icon: '📦',
      subtitle: 'Ej: Calamar — formato "caja 5 kg" — 32 € → coste 6,40 €/kg',
      fields: [
        { name: 'nombre', label: 'Nombre', required: true, value: p?.nombre || '', placeholder: 'Calamar' },
        { name: 'categoria', label: 'Categoría', type: 'select', options: CATEGORIAS, value: p?.categoria || 'Otros' },
        { name: 'formato', label: 'Formato de compra', value: p?.formato || '', placeholder: 'caja 5 kg' },
        { name: 'precioCompra', label: 'Precio de compra (€)', type: 'number', required: true, value: p?.precio_compra ?? '', placeholder: '32.00' },
        { name: 'cantidadFormato', label: '¿Cuánto trae? (número)', type: 'number', required: true, value: p?.cantidad_formato ?? '', placeholder: '5', help: 'Si la caja trae 5 kg pon 5. Si el paquete trae 24 alitas pon 24.' },
        { name: 'unidad', label: 'Unidad', type: 'select', options: UNIDADES, value: p?.unidad || 'kg' },
        { name: 'notas', label: 'Notas', value: p?.notas || '' },
      ],
      onSubmit: async (v) => {
        const body = { nombre: v.nombre, categoria: v.categoria, formato: v.formato, precioCompra: Number(v.precioCompra), cantidadFormato: Number(v.cantidadFormato), unidad: v.unidad, notas: v.notas };
        if (p) await apiClient.put('/api/v1/bodega/productos/' + p.id, body);
        else await apiClient.post('/api/v1/bodega/productos', body);
        toast(p ? 'Producto actualizado' : 'Producto añadido');
        await load(); draw();
      },
    });
  }

  async function delProducto(p) {
    const ok = await openConfirm({ title: 'Borrar "' + p.nombre + '"', message: 'Se quitará también de los escandallos que lo usen.', danger: true });
    if (!ok) return;
    await apiClient.delete('/api/v1/bodega/productos/' + p.id);
    toast('Producto borrado'); await load(); draw();
  }

  // ---------- ESCANDALLOS ----------
  function drawEscandallos() {
    if (!state.platos.length) {
      return emptyState('🍽️', 'Sin platos', 'Añade los platos de la carta con su precio de venta.', btn('+ Añadir plato', () => openPlatoModal(), {}));
    }
    const list = h('div', { class: 'space-y-3' });
    const cards = [];
    for (const pl of state.platos) {
      const card = platoCard(pl);
      card.dataset.q = (pl.nombre || '').toLowerCase();
      cards.push(card);
      list.appendChild(card);
    }
    return h('div', {}, [ searchBox('🔎 Buscar plato…', cards), list ]);
  }

  function platoCard(pl) {
    const tienePvp = pl.precio_racion !== null && pl.precio_racion !== undefined;
    let margenBadge;
    if (pl.margen_pct === null) {
      margenBadge = h('span', { class: 'text-xs text-ink-400' }, 'sin datos');
    } else {
      const bg = pl.margen_pct >= 65 ? 'bg-emerald-600' : pl.margen_pct >= 50 ? 'bg-amber-600' : 'bg-red-600';
      const soft = pl.margen_pct >= 65 ? 'text-emerald-100' : pl.margen_pct >= 50 ? 'text-amber-100' : 'text-red-100';
      margenBadge = h('span', { class: 'text-xs font-bold px-2 py-1 rounded-lg whitespace-nowrap ' + bg }, [
        h('span', { class: soft }, 'Ganas '),
        h('span', { class: 'text-white' }, eur(pl.margen_racion) + ' (' + pl.margen_pct.toFixed(0) + '%)'),
      ]);
    }

    // Porción total preestablecida (suma de gramos + unidades)
    let _g = 0, _u = 0;
    for (const ing of (pl.ingredientes || [])) {
      if ((ing.unidad || '') === 'kg') _g += Number(ing.cantidad) * 1000; else _u += Number(ing.cantidad);
    }
    const porcion = [ _g ? Math.round(_g) + ' g' : null, _u ? _u + ' ud' : null ].filter(Boolean).join(' · ');

    const body = h('div', { class: 'hidden px-4 pb-4 space-y-2' });
    const header = h('button', { class: 'w-full flex items-start justify-between gap-2 px-4 py-3 text-left', onclick: () => body.classList.toggle('hidden') }, [
      h('div', { class: 'min-w-0 flex-1' }, [
        h('div', { class: 'font-semibold text-ink-100 break-words' }, [
          pl.nombre,
          porcion ? h('span', { class: 'ml-2 text-xs font-normal text-gold-300' }, '· ' + porcion) : null,
        ]),
        h('div', { class: 'text-xs text-ink-400 break-words' }, [
          pl.precio_tapa ? 'Tapa ' + eur(pl.precio_tapa) + ' · ' : '',
          tienePvp ? 'Ración ' + eur(pl.precio_racion) : '',
          pl.precio_unidad ? ' · ' + eur(pl.precio_unidad) + '/ud' : '',
          pl.coste_racion !== null ? ' · Coste ración ' + eur(pl.coste_racion) : '',
        ].join('')),
      ]),
      h('div', { class: 'flex items-center gap-2 shrink-0' }, [margenBadge, h('span', { class: 'text-ink-400' }, '▾')]),
    ]);

    for (const ing of pl.ingredientes) {
      const ingActions = [ h('span', { class: 'text-gold-300' }, eur(ing.coste)) ];
      if (!RO) {
        ingActions.push(iconBtn('✏️', () => editIngrediente(pl, ing)));
        ingActions.push(iconBtn('🗑️', async () => { await apiClient.delete('/api/v1/bodega/ingredientes/' + ing.id); toast('Ingrediente quitado'); await load(); draw(); }));
      }
      body.appendChild(h('div', { class: 'flex items-center justify-between bg-base-800/60 rounded-lg px-3 py-2 text-sm' }, [
        h('span', { class: 'text-ink-200' }, ing.producto_nombre + ' — ' + ing.cantidad + ' ' + (ing.unidad || 'ud')),
        h('div', { class: 'flex items-center gap-3' }, ingActions),
      ]));
    }
    if (!RO) {
      body.appendChild(h('div', { class: 'flex gap-2 pt-1' }, [
        btn('+ Ingrediente', () => addIngrediente(pl), {}),
        btn('✏️ Editar plato', () => openPlatoModal(pl), {}),
        btn('🗑️ Borrar', async () => {
          const ok = await openConfirm({ title: 'Borrar "' + pl.nombre + '"', danger: true });
          if (!ok) return;
          await apiClient.delete('/api/v1/bodega/platos/' + pl.id); toast('Plato borrado'); await load(); draw();
        }, {}),
      ]));
    }

    return h('div', { class: 'bg-base-900 border border-base-700 rounded-2xl overflow-hidden' }, [header, body]);
  }

  async function addIngrediente(pl) {
    if (!state.productos.length) { toast('Primero añade productos con sus costes', 'error'); return; }
    await openModal({
      title: 'Ingrediente para ' + pl.nombre, icon: '🧮',
      fields: [
        { name: 'productoId', label: 'Producto', type: 'select', required: true, options: state.productos.map((p) => ({ value: p.id, label: p.nombre + ' (' + eur(p.coste_unitario) + '/' + (p.unidad || 'ud') + ')' })) },
        { name: 'cantidad', label: 'Cantidad por RACIÓN (en la unidad del producto)', type: 'number', required: true, placeholder: '0.200', help: 'Ej: 200 g de calamar = 0.2 (si el producto está en kg)' },
      ],
      onSubmit: async (v) => {
        await apiClient.post('/api/v1/bodega/platos/' + pl.id + '/ingredientes', { productoId: v.productoId, cantidad: Number(v.cantidad) });
        toast('Ingrediente añadido'); await load(); draw();
      },
    });
  }

  async function editIngrediente(pl, ing) {
    await openModal({
      title: ing.producto_nombre + ' en ' + pl.nombre, icon: '🧮',
      fields: [{ name: 'cantidad', label: 'Cantidad por ración', type: 'number', required: true, value: ing.cantidad }],
      onSubmit: async (v) => {
        await apiClient.put('/api/v1/bodega/ingredientes/' + ing.id, { cantidad: Number(v.cantidad) });
        toast('Cantidad actualizada'); await load(); draw();
      },
    });
  }

  async function openPlatoModal(pl) {
    await openModal({
      title: pl ? 'Editar plato' : 'Nuevo plato', icon: '🍽️',
      fields: [
        { name: 'nombre', label: 'Nombre', required: true, value: pl?.nombre || '' },
        { name: 'precioTapa', label: 'Precio tapa (€)', type: 'number', value: pl?.precio_tapa ?? '' },
        { name: 'precioRacion', label: 'Precio ración (€)', type: 'number', value: pl?.precio_racion ?? '' },
        { name: 'precioUnidad', label: 'Precio por unidad (€)', type: 'number', value: pl?.precio_unidad ?? '', help: 'Solo para alitas, muslitos, tigres…' },
        { name: 'notas', label: 'Notas', value: pl?.notas || '' },
      ],
      onSubmit: async (v) => {
        const body = {
          nombre: v.nombre,
          precioTapa: v.precioTapa === '' ? null : Number(v.precioTapa),
          precioRacion: v.precioRacion === '' ? null : Number(v.precioRacion),
          precioUnidad: v.precioUnidad === '' ? null : Number(v.precioUnidad),
          notas: v.notas,
        };
        if (pl) await apiClient.put('/api/v1/bodega/platos/' + pl.id, body);
        else await apiClient.post('/api/v1/bodega/platos', body);
        toast(pl ? 'Plato actualizado' : 'Plato añadido');
        await load(); draw();
      },
    });
  }

  // ---------- COSTE POR TAPA ----------
  function drawCostesTapa() {
    const box = h('div', { class: 'space-y-5' });
    box.appendChild(h('div', { class: 'bg-gold-400/10 border border-gold-400/20 rounded-xl px-4 py-3 text-sm text-ink-300' },
      'Coste real por gramo y por tapa. Los precios de compra los editas en la pestaña "Productos" y aquí se recalcula todo solo. Cantidades de tapa orientativas de barra.'));

    const cont = h('div', {});
    cont.appendChild(h('div', { class: 'flex items-center justify-center py-6 text-ink-400 text-sm' }, 'Cargando costes…'));
    box.appendChild(cont);

    apiClient.get('/api/v1/bodega/productos').then((d) => {
      const prods = (d.productos || []);
      cont.innerHTML = '';
      const porKg = prods.filter((p) => (p.unidad || '') === 'kg');
      const otros = prods.filter((p) => (p.unidad || '') !== 'kg');

      // Calculadora
      cont.appendChild(calcTapa(porKg));

      // Tarjetas por gramos (móvil-first)
      if (porKg.length) {
        const miniStat = (label, val) => h('div', { class: 'bg-base-800/70 rounded-lg px-2 py-1.5 text-center' }, [
          h('div', { class: 'text-[9px] text-ink-400 uppercase tracking-wide leading-tight' }, label),
          h('div', { class: 'text-[13px] font-semibold text-gold-200 mt-0.5' }, val),
        ]);
        const cards = porKg.sort((a, b) => (a.precio_compra - b.precio_compra)).map((p) => {
          const kg = Number(p.precio_compra) || 0;
          const g = kg / 1000;
          const est = p.notas && p.notas.toLowerCase().includes('estim');
          const body = h('div', { class: 'grid grid-cols-3 gap-2 mt-2 hidden' }, [
            miniStat('1 gramo', cent(g)),
            miniStat('10 gramos', cent(g * 10)),
            miniStat('Tapa 40 g', eur(g * 40)),
            miniStat('½ ración 100 g', eur(g * 100)),
            miniStat('Ración 200 g', eur(g * 200)),
            est ? miniStat('⚠️', 'estimado') : h('div'),
          ]);
          const chevron = h('span', { class: 'text-ink-400 text-xs transition' }, '▾');
          const header = h('button', {
            class: 'w-full flex items-center justify-between gap-2 text-left',
            onclick: () => { body.classList.toggle('hidden'); chevron.textContent = body.classList.contains('hidden') ? '▾' : '▴'; },
          }, [
            h('div', { class: 'font-semibold text-ink-100 break-words min-w-0 text-sm' }, p.nombre + (est ? ' ⚠️' : '')),
            h('div', { class: 'flex items-center gap-2 shrink-0' }, [
              h('div', { class: 'text-gold-300 font-bold text-sm' }, eur(kg) + '/kg'),
              chevron,
            ]),
          ]);
          const card = h('div', { class: 'bg-base-900 border border-base-700 rounded-xl p-3' }, [header, body]);
          card.dataset.q = (p.nombre || '').toLowerCase();
          return card;
        });
        cont.appendChild(h('div', { class: 'mt-5' }, [
          h('div', { class: 'text-sm font-semibold text-gold-300 mb-2' }, '📋 Coste de cada producto (por peso)'),
          searchBox('🔎 Buscar producto…', cards),
          h('div', { class: 'space-y-2' }, cards),
        ]));
      }

      // Otros (por unidad/lata) como tarjetas
      if (otros.length) {
        const cards = otros.map((p) => h('div', { class: 'bg-base-900 border border-base-700 rounded-xl p-3 flex items-center justify-between' }, [
          h('div', {}, [
            h('div', { class: 'text-ink-100 font-medium' }, p.nombre),
            h('div', { class: 'text-xs text-ink-400' }, p.formato || p.unidad || '—'),
          ]),
          h('div', { class: 'text-gold-300 font-bold' }, eur(p.precio_compra)),
        ]));
        cont.appendChild(h('div', { class: 'mt-5' }, [
          h('div', { class: 'text-sm font-semibold text-gold-300 mb-2' }, '🥫 Por unidad (latas, botellines, cañas…)'),
          h('div', { class: 'space-y-2' }, cards),
        ]));
      }
    }).catch(() => { cont.innerHTML = ''; cont.appendChild(h('div', { class: 'text-red-300 text-sm' }, 'No se pudieron cargar los productos.')); });

    return box;
  }

  function calcTapa(porKg) {
    const wrapC = h('div', { class: 'bg-base-900 border border-gold-400/30 rounded-2xl p-5' });
    wrapC.appendChild(h('div', { class: 'font-display font-bold text-ink-100' }, '🧮 Calculadora de tapa'));
    wrapC.appendChild(h('div', { class: 'text-xs text-ink-400 mb-3' }, 'Elige el producto, escribe los gramos que pones y te dice lo que te cuesta esa tapa.'));
    if (!porKg.length) { wrapC.appendChild(h('div', { class: 'text-ink-400 text-sm' }, 'Añade productos en kg para calcular.')); return wrapC; }
    const sel = h('select', { class: 'w-full max-w-full bg-base-800 border border-base-700 rounded-lg px-3 py-2 text-sm' },
      porKg.map((p) => { const o = document.createElement('option'); o.value = p.id; o.textContent = p.nombre + ' (' + eur(p.precio_compra) + '/kg)'; return o; }));
    const gramos = h('input', { type: 'number', value: '40', min: '0', class: 'bg-base-800 border border-base-700 rounded-lg px-3 py-2 text-sm w-28', placeholder: 'gramos' });
    const out = h('div', { class: 'text-2xl font-display font-bold text-gold-300' }, '—');
    function calc() {
      const p = porKg.find((x) => x.id === sel.value);
      const g = Number(gramos.value) || 0;
      if (!p) { out.textContent = '—'; return; }
      const coste = (Number(p.precio_compra) / 1000) * g;
      out.textContent = eur(coste);
    }
    sel.addEventListener('change', calc); gramos.addEventListener('input', calc);
    setTimeout(calc, 0);
    wrapC.appendChild(h('div', { class: 'space-y-3' }, [
      h('div', { class: 'min-w-0' }, [ h('label', { class: 'block text-[10px] text-ink-400 uppercase mb-0.5' }, 'Producto'), sel ]),
      h('div', { class: 'flex items-end justify-between gap-3' }, [
        h('div', {}, [ h('label', { class: 'block text-[10px] text-ink-400 uppercase mb-0.5' }, 'Gramos' ), gramos ]),
        h('div', { class: 'text-right' }, [ h('label', { class: 'block text-[10px] text-ink-400 uppercase mb-0.5' }, 'Coste' ), out ]),
      ]),
    ]));
    return wrapC;
  }

  const cent = (eurVal) => {
    const c = (Number(eurVal) || 0) * 100; // valor en céntimos
    if (c < 1) return c.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' centavos';
    return c.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + ' céntimos';
  };
  const tdr = (c) => h('td', { class: 'px-4 py-3 text-sm text-gold-200 text-right whitespace-nowrap' }, c);

  // ---------- CARTA PÚBLICA ----------
  const WEB_URL = 'https://carta.178-105-147-16.sslip.io/';
  const CARTA_URL = 'https://carta.178-105-147-16.sslip.io/carta.html';
  const QR_URL = 'https://carta.178-105-147-16.sslip.io/img/qr.png';
  const PDF_URL = 'https://carta.178-105-147-16.sslip.io/Carta-Bodega-7-Hermanos.pdf';

  function drawCarta() {
    const box = h('div', { class: 'space-y-5' });

    // --- Cabecera: QR + accesos ---
    const top = h('div', { class: 'bg-base-900 border border-base-700 rounded-2xl p-5 flex flex-col md:flex-row gap-5 items-center' }, [
      h('div', { class: 'text-center' }, [
        h('img', { src: QR_URL, alt: 'QR carta', class: 'w-40 h-40 rounded-lg bg-white p-2 mx-auto' }),
        h('div', { class: 'text-xs text-ink-400 mt-2' }, 'QR fijo de las mesas'),
      ]),
      h('div', { class: 'flex-1 space-y-3 text-center md:text-left' }, [
        h('div', { class: 'text-lg font-display font-bold text-ink-100' }, '🍷 Web y carta de Bodega 7 Hermanos'),
        h('div', { class: 'text-sm text-ink-400' }, 'La web (historia, carta, reservas, reseñas y mapa) y la carta pública. El QR de las mesas y estos enlaces NO cambian; lo que edites abajo se refleja al instante en la carta.'),
        h('div', { class: 'text-xs bg-base-800 rounded-lg px-3 py-2 text-gold-300 break-all font-mono' }, WEB_URL),
        h('div', { class: 'flex gap-2 flex-wrap justify-center md:justify-start' }, [
          h('a', { href: WEB_URL, target: '_blank', rel: 'noopener', class: 'bg-gradient-to-r from-gold-400 to-gold-500 text-base-900 font-semibold px-4 py-2 rounded-lg text-sm' }, '🌐 Ver web'),
          h('a', { href: CARTA_URL, target: '_blank', rel: 'noopener', class: 'bg-base-800 hover:bg-base-700 border border-base-700 text-ink-100 px-4 py-2 rounded-lg text-sm' }, '🍷 Ver carta'),
          h('a', { href: PDF_URL, target: '_blank', rel: 'noopener', class: 'bg-base-800 hover:bg-base-700 border border-base-700 text-ink-100 px-4 py-2 rounded-lg text-sm' }, '📄 PDF imprimir'),
          h('a', { href: QR_URL, download: 'QR-bodega-7-hermanos.png', target: '_blank', rel: 'noopener', class: 'bg-base-800 hover:bg-base-700 border border-base-700 text-ink-100 px-4 py-2 rounded-lg text-sm' }, '⬇️ Descargar QR'),
        ]),
      ]),
    ]);
    box.appendChild(top);

    // En solo-lectura no se muestra el editor (solo QR, enlace y PDF de arriba).
    if (RO) {
      box.appendChild(h('div', { class: 'bg-base-800 border border-base-700 rounded-2xl p-5 text-sm text-ink-300 text-center' },
        '👀 Vista de consulta. Para ver la carta pulsa "Ver carta" o descarga el PDF arriba.'));
      return box;
    }

    // --- Editor ---
    const editor = h('div', { class: 'bg-base-900 border border-base-700 rounded-2xl p-5' });
    editor.appendChild(h('div', { class: 'flex items-center justify-center py-6 text-ink-400 text-sm' }, 'Cargando carta editable…'));
    box.appendChild(editor);

    apiClient.get('/api/v1/bodega/carta-data').then((data) => {
      state.carta = data;
      renderEditor(editor, data);
    }).catch(() => {
      editor.innerHTML = '';
      editor.appendChild(h('div', { class: 'text-red-300 text-sm' }, 'No se pudo cargar la carta editable.'));
    });

    return box;
  }

  const SECCIONES = [
    { key: 'tapas', label: 'Tapas y Raciones', type: 'p' },
    { key: 'bocadillos_frios', label: 'Bocadillos fríos', type: 'me' },
    { key: 'bocadillos_calientes', label: 'Bocadillos calientes', type: 'me' },
    { key: 'tablas', label: 'Tablas', type: 'p' },
    { key: 'cerveza', label: 'Cerveza', type: 'p' },
    { key: 'vermut_vino', label: 'Vermut y Vino', type: 'p' },
    { key: 'sidra_copas', label: 'Sidra y Copas', type: 'p' },
    { key: 'refrescos', label: 'Refrescos', type: 'p' },
    { key: 'cafes', label: 'Cafés', type: 'p' },
    { key: 'desayunos', label: 'Desayunos', type: 'p' },
  ];

  function renderEditor(editor, data) {
    editor.innerHTML = '';
    editor.appendChild(h('div', { class: 'flex items-center justify-between mb-4 sticky top-0 bg-base-900 py-2 z-10' }, [
      h('div', { class: 'font-display font-bold text-ink-100' }, '✏️ Editar carta'),
      btn('💾 Guardar cambios', () => saveCarta(data), {}),
    ]));

    // Promo
    if (data.promo) {
      const p = data.promo;
      editor.appendChild(sectionTitle('Promoción destacada'));
      editor.appendChild(h('div', { class: 'grid grid-cols-1 md:grid-cols-3 gap-2 mb-4' }, [
        inp('Epígrafe', p.k, (v) => p.k = v),
        inp('Texto', p.t, (v) => p.t = v),
        inp('Precio', p.e, (v) => p.e = v),
      ]));
    }

    for (const sec of SECCIONES) {
      const arr = data[sec.key];
      if (!Array.isArray(arr)) continue;
      editor.appendChild(sectionTitle(sec.label));
      const wrapS = h('div', { class: 'space-y-1 mb-4' });
      arr.forEach((it) => {
        const cells = [ inp('Nombre', it.n, (v) => it.n = v, 'flex-1') ];
        if (sec.type === 'me') {
          cells.push(inp('Medio', it.m != null ? it.m : '', (v) => it.m = v || null, 'w-20'));
          cells.push(inp('Entero', it.e != null ? it.e : '', (v) => it.e = v || null, 'w-20'));
        } else {
          cells.push(inp('Precio', it.p != null ? it.p : '', (v) => it.p = v, 'w-24'));
        }
        wrapS.appendChild(h('div', { class: 'flex gap-2 items-end' }, cells));
      });
      editor.appendChild(wrapS);
    }

    if (data.bocadillos_nota !== undefined) {
      editor.appendChild(sectionTitle('Nota de bocadillos'));
      editor.appendChild(h('div', { class: 'mb-4' }, inp('Nota', data.bocadillos_nota, (v) => data.bocadillos_nota = v, 'w-full')));
    }

    editor.appendChild(h('div', { class: 'flex justify-end pt-2 border-t border-base-700' }, btn('💾 Guardar cambios', () => saveCarta(data), {})));
  }

  function sectionTitle(t) {
    return h('div', { class: 'text-xs font-semibold text-gold-300 uppercase tracking-wide border-b border-base-700 pb-1 mb-2 mt-2' }, t);
  }

  function inp(label, value, onInput, cls) {
    const input = h('input', { type: 'text', value: value == null ? '' : value, placeholder: label,
      class: (cls || 'flex-1') + ' bg-base-800 border border-base-700 focus:border-gold-400 outline-none rounded-lg px-3 py-2 text-sm' });
    input.addEventListener('input', () => onInput(input.value));
    return h('div', { class: cls && cls.startsWith('w-') ? cls : 'flex-1' }, [
      h('label', { class: 'block text-[10px] text-ink-400 uppercase tracking-wide mb-0.5' }, label),
      input,
    ]);
  }

  async function saveCarta(data) {
    try {
      await apiClient.put('/api/v1/bodega/carta-data', data);
      toast('Carta guardada ✓ Ya está actualizada para los clientes');
    } catch (e) {
      toast('Error al guardar la carta', 'error');
    }
  }

  // ---------- helpers ----------
  function searchBox(placeholder, items) {
    const input = h('input', { type: 'text', placeholder: placeholder,
      class: 'flex-1 bg-base-800 border border-base-700 focus:border-gold-400 focus:ring-1 focus:ring-gold-400/30 outline-none rounded-lg px-3 py-2 text-sm' });
    const counter = h('span', { class: 'text-xs text-ink-400 ml-2' }, '');
    input.addEventListener('input', () => {
      const q = input.value.trim().toLowerCase();
      let vis = 0;
      items.forEach((el) => {
        const hay = (el.dataset && el.dataset.q ? el.dataset.q : el.textContent.toLowerCase());
        const show = !q || hay.includes(q);
        el.style.display = show ? '' : 'none';
        if (show) vis++;
      });
      counter.textContent = q ? vis + ' resultado' + (vis === 1 ? '' : 's') : '';
    });
    return h('div', { class: 'flex items-center' }, [ input, counter ]);
  }
  const td = (c) => h('td', { class: 'px-4 py-3 text-sm text-ink-200' }, c);
  const iconBtn = (emoji, onclick) => h('button', { class: 'text-ink-400 hover:text-ink-100 transition text-sm', onclick }, emoji);
  function tableCard(headers, rows) {
    return h('div', { class: 'bg-base-900 border border-base-700 rounded-2xl overflow-x-auto' },
      h('table', { class: 'w-full' }, [
        h('thead', {}, h('tr', { class: 'border-b border-base-700' },
          headers.map((x) => h('th', { class: 'px-4 py-3 text-left text-xs font-semibold text-ink-400 uppercase tracking-wide' }, x)))),
        h('tbody', {}, rows),
      ]));
  }

  wrap.appendChild(skeletonCard(4));
  await load();
  draw();
}
